/**
 * 枚举值 → 中文业务名的唯一正式映射工具。
 * 契约：普通页面只显示中文业务名；未知值显示中性中文文案（如「状态待识别」
 * 「需人工确认」），不得把原始技术代码（如 weird_status、api.calls）回显给
 * 普通操作者。原始技术值只允许进入授权的技术证据区（audit.read 等）。
 */

/** 未知状态值的统一产品化文案。 */
export const UNKNOWN_STATUS_TEXT = "状态待识别";
/** 其他未知枚举值的统一产品化文案。 */
export const UNKNOWN_ENUM_TEXT = "需人工确认";

/**
 * 判断一个值是否是「代码型」技术值：仅由 ASCII 字母、数字与 . _ : - 组成，
 * 不含中文、空格或其他人类可读业务字符。
 */
export function isTechnicalCode(value: string): boolean {
  return /^[a-z0-9][a-z0-9_.:-]*$/i.test(value);
}

/**
 * 枚举值 → 中文业务名。
 * - 已知值：返回映射表中的中文名；
 * - 未知代码型值：返回中性中文文案，不泄漏原始代码；
 * - 非代码型值（用户自填的中文业务名）：原样显示。
 */
export function businessLabel(labels: Record<string, string>, value: string, unknownText: string = UNKNOWN_ENUM_TEXT): string {
  const known = labels[value];
  if (known !== undefined) return known;
  return isTechnicalCode(value) ? unknownText : value;
}

/** 状态枚举的统一中文映射（StatusBadge 与文本场景共用同一份）。 */
export const statusLabels: Record<string, string> = {
  category_current: "当前发布版", category_history: "历史发布版", category_draft: "草稿",
  active: "运行中", ready: "就绪", ok: "正常", provisioning: "配置中", suspended: "已暂停", deleted: "已删除", archived: "已归档",
  revoked: "已吊销", expired: "已过期", rotating: "轮换中", pending: "待审批", approved: "已批准", rejected: "已拒绝", provisioned: "已进入接入",
  healthy: "正常", delivered: "已送达", processing: "投递中", failed: "失败", dead: "已停止重试", open: "处理中", monitoring: "观察中", resolved: "已解决",
  enabled: "已启用", disabled: "已停用", defaults: "默认策略", trialing: "试用中", past_due: "待支付", canceled: "已取消", frozen: "已冻结",
  verified: "已验证", unverified: "未验证", unchecked: "未验证",
  awaiting_activation: "等待客户激活", grace_period: "宽限期", pending_cancel: "待取消", activation_error: "生效异常",
  purged: "已永久注销", merged: "已合并处理",
  configured: "配置已保存", not_configured: "未配置",
  draft: "草稿", pending_publish: "待发布", published: "已发布", stop_new_sales: "停止新购", paused: "已暂停", retired: "已停用",
  pending_payment: "待支付", pending_offline_confirm: "待确认收款", paid: "已收款", completed: "已完成", refunded: "已退款",
  partially_refunded: "部分退款", void: "已作废", uncollectible: "无法收回", succeeded: "已到账",
};

/** 状态枚举 → 中文；未知状态统一显示「状态待识别」，不回显原始代码。 */
export function statusText(value: string): string {
  return statusLabels[value] ?? UNKNOWN_STATUS_TEXT;
}
